import { isVectorType } from "@/lib/node-description/value-types"
import type { NodeParameter } from "./parameter-utils"

export type SentenceSegment =
	| { key: string; type: "text"; content: string }
	| { key: string; type: "parameter"; name: string; showLabel: boolean }

export const buildSegments = (
	description: string,
	parameters: NodeParameter[],
): SentenceSegment[] => {
	const parameterByName = new Map(
		parameters.map((param) => [param.name, param]),
	)
	const segments: SentenceSegment[] = []
	const renderedVectors = new Set<string>()

	const placeholderRegex = /\{([^}]+)\}/g
	let cursor = 0
	for (;;) {
		const match = placeholderRegex.exec(description)
		if (!match) {
			break
		}
		if (match.index > cursor) {
			segments.push({
				key: `text:${cursor}`,
				type: "text",
				content: description.slice(cursor, match.index),
			})
		}

		const token = match[1] ?? ""
		const [baseName, component] = token.split(".")
		if (!baseName) {
			cursor = match.index + match[0].length
			continue
		}

		const parameter = parameterByName.get(baseName)
		if (!parameter) {
			cursor = match.index + match[0].length
			continue
		}

		if (component) {
			if (component !== "x" || renderedVectors.has(baseName)) {
				cursor = match.index + match[0].length
				continue
			}
			renderedVectors.add(baseName)
			segments.push({
				key: `param:${baseName}:${match.index}`,
				type: "parameter",
				name: baseName,
				showLabel: true,
			})
			cursor = match.index + match[0].length
			continue
		}

		if (isVectorType(parameter.type) && renderedVectors.has(baseName)) {
			cursor = match.index + match[0].length
			continue
		}

		segments.push({
			key: `param:${baseName}:${match.index}`,
			type: "parameter",
			name: baseName,
			showLabel: false,
		})

		cursor = match.index + match[0].length
	}

	if (cursor < description.length) {
		segments.push({
			key: `text:${cursor}`,
			type: "text",
			content: description.slice(cursor),
		})
	}

	return segments
}
